import { useEffect, useRef, useState, type CSSProperties } from "react";
import {
  ArrowRight,
  Check,
  ChevronRight,
  Database,
  FileSpreadsheet,
  FileText,
  LockKeyhole,
  UploadCloud,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { savePendingFiles } from "../lib/pendingFiles";
import { GradientBackground } from "./GradientBackground";

type PublicCms = {
  site_content: { key: string; value: string }[];
  blog_posts: {
    slug: string;
    title: string;
    category: string;
    excerpt: string;
    content: string;
    published_at?: string;
  }[];
  faq_items: { question: string; answer: string }[];
  design_settings: { key: string; value: string }[];
};

function usePublicCms() {
  const [cms, setCms] = useState<PublicCms | null>(null);
  useEffect(() => {
    api<PublicCms>("/v1/content/site")
      .then(setCms)
      .catch(() => undefined);
  }, []);
  return cms;
}

export function BideBrand() {
  return (
    <Link
      to="/"
      className="bide-brand"
      aria-label="Bulk Invoice Data Extractor home"
    >
      <span className="bide-brand-mark">
        <img src="/bulk-invoice-data-extractor-logo.png" alt="" />
      </span>
      <strong>Bulk Invoice Data Extractor</strong>
    </Link>
  );
}

export function BideHeader() {
  return (
    <header className="bide-header">
      <BideBrand />
      <nav aria-label="Main navigation">
        <Link to="/#how">How it works</Link>
        <Link to="/#example">Example</Link>
        <Link to="/#faq">FAQ</Link>
        <Link className="header-button header-signin" to="/login">
          Sign in
        </Link>
        <Link className="header-button header-signup" to="/signup">
          Sign up
        </Link>
      </nav>
    </header>
  );
}

function formatBytes(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function PublicDropZone() {
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState("");
  const picker = useRef<HTMLInputElement>(null);

  function accept(incoming: FileList | File[]) {
    const candidates = Array.from(incoming);
    const invalid = candidates.find(
      (file) =>
        file.type !== "application/pdf" &&
        !file.name.toLowerCase().endsWith(".pdf"),
    );
    if (invalid) {
      setError(`${invalid.name} is not a PDF.`);
      return;
    }
    const merged = [...files, ...candidates].filter(
      (file, index, all) =>
        all.findIndex(
          (match) => match.name === file.name && match.size === file.size,
        ) === index,
    );
    if (merged.length > 300) {
      setError("You can select up to 300 PDFs at a time.");
      return;
    }
    setFiles(merged);
    void savePendingFiles(merged);
    setError("");
  }

  return (
    <section
      className={`public-drop ${dragging ? "is-dragging" : ""}`}
      aria-label="Upload PDF invoices"
    >
      <div
        className="public-drop-inner"
        onDragEnter={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node))
            setDragging(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          accept(event.dataTransfer.files);
        }}
      >
        <span className="drop-icon">
          <UploadCloud size={25} />
        </span>
        <p className="drop-kicker">Start with your own invoices</p>
        <h2>Drop PDFs here</h2>
        <p>or choose files from your computer</p>
        <button
          className="choose-files"
          type="button"
          onClick={() => picker.current?.click()}
        >
          Choose PDF files
        </button>
        <input
          ref={picker}
          type="file"
          accept="application/pdf,.pdf"
          multiple
          hidden
          onChange={(event) => event.target.files && accept(event.target.files)}
        />
        <div className="drop-formats">
          <span>PDF</span>
          <span>Up to 300 files</span>
          <span>50 MB each</span>
        </div>
      </div>
      {error && (
        <p className="drop-error" role="alert">
          {error}
        </p>
      )}
      {files.length > 0 && (
        <div className="selected-files">
          <div className="selected-summary">
            <span>
              <FileText size={17} />
              <strong>
                {files.length} {files.length === 1 ? "PDF" : "PDFs"} ready
              </strong>
            </span>
            <button
              type="button"
              onClick={() => {
                setFiles([]);
                void savePendingFiles([]);
              }}
            >
              Clear
            </button>
          </div>
          {files.slice(0, 3).map((file) => (
            <div className="selected-file" key={`${file.name}-${file.size}`}>
              <FileText size={16} />
              <span>
                {file.name}
                <small>{formatBytes(file.size)}</small>
              </span>
              <button
                type="button"
                aria-label={`Remove ${file.name}`}
                onClick={() =>
                  setFiles((current) => {
                    const next = current.filter((item) => item !== file);
                    void savePendingFiles(next);
                    return next;
                  })
                }
              >
                <X size={15} />
              </button>
            </div>
          ))}
          {files.length > 3 && (
            <p className="more-files">and {files.length - 3} more</p>
          )}
          <div className="continue-gate">
            <p>
              <LockKeyhole size={15} /> Sign in before anything is uploaded or
              processed.
            </p>
            <div>
              <Link to="/login?returnTo=%2Fapp%2Fdatasets%2Fnew">Sign in</Link>
              <Link
                className="continue-signup"
                to="/signup?returnTo=%2Fapp%2Fdatasets%2Fnew"
              >
                Sign up to continue
              </Link>
            </div>
          </div>
        </div>
      )}
      <p className="drop-privacy">
        <LockKeyhole size={13} /> Your files stay on this device until you
        continue.
      </p>
    </section>
  );
}

export function LandingV2() {
  const cms = usePublicCms();
  const content = Object.fromEntries(
    cms?.site_content.map((item) => [item.key, item.value]) ?? [],
  );
  const design = Object.fromEntries(
    cms?.design_settings.map((item) => [item.key, item.value]) ?? [],
  );
  const livePosts = cms?.blog_posts.length
    ? cms.blog_posts.map((post) => ({
        ...post,
        date: post.published_at
          ? new Date(post.published_at).toLocaleDateString("en", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })
          : "Draft",
        read: `${Math.max(1, Math.ceil(post.content.split(/\s+/).length / 220))} min read`,
        body: post.content.split(/\n\s*\n/).filter(Boolean),
      }))
    : blogPosts;
  const steps = [
    ["01", "Upload", "Drop in one invoice or a folder of up to 300 PDFs."],
    [
      "02",
      "Pick your fields",
      "The extractor suggests useful fields from your documents.",
    ],
    ["03", "Review", "Only uncertain values are brought to your attention."],
    ["04", "Export", "Download one clean Excel, CSV, Sheets or JSON dataset."],
  ];
  return (
    <main
      className="bide-landing"
      style={
        {
          "--bide-primary": String(design.primary_color ?? "#2f7d59"),
          "--bide-dark": String(design.dark_color ?? "#123e2f"),
          "--bide-paper": String(design.paper_color ?? "#f8f5ed"),
          "--bide-radius": String(design.corner_radius ?? "12px"),
        } as CSSProperties
      }
    >
      <BideHeader />
      <section className="bide-hero">
        <GradientBackground className="bide-hero-bg" />
        <div className="hero-copy">
          <span className="bide-eyebrow">
            {content.hero_eyebrow ??
              "Invoice data extraction, without the busywork"}
          </span>
          <h1>
            {content.hero_title ?? "Turn invoices into clean, usable data."}
          </h1>
          <p>
            {content.hero_description ??
              "Upload invoices in any layout. Bulk Invoice Data Extractor captures the fields you need, flags anything uncertain, and builds one spreadsheet ready for your workflow."}
          </p>
          <div className="hero-proof">
            <span>
              <FileSpreadsheet size={17} /> Excel, CSV, Sheets & JSON
            </span>
            <span>
              <LockKeyhole size={17} /> Private by design
            </span>
          </div>
        </div>
        <PublicDropZone />
      </section>
      <section id="how" className="bide-section bide-how">
        <div className="bide-section-heading">
          <span className="bide-eyebrow">
            A shorter path from PDF to spreadsheet
          </span>
          <h2>Four clear steps. No manual retyping.</h2>
        </div>
        <div className="bide-steps">
          {steps.map(([number, title, description]) => (
            <article key={number}>
              <span>{number}</span>
              <h3>{title}</h3>
              <p>{description}</p>
            </article>
          ))}
        </div>
      </section>
      <BeforeAfter
        title={content.example_title}
        imageUrl={content.example_image_url}
      />
      <FaqSection items={cms?.faq_items} title={content.faq_title} />
      <BlogPreview posts={livePosts} />
      <section className="bide-final-cta">
        <span className="bide-eyebrow">
          Your first dataset is a few clicks away
        </span>
        <h2>Let invoices fill the spreadsheet for you.</h2>
        <p>Start with one PDF, then scale to hundreds when you are ready.</p>
        <Link to="/signup">
          Create a free account <ArrowRight size={17} />
        </Link>
      </section>
      <BideFooter />
    </main>
  );
}

const faqItems = [
  [
    "Do I need an account to try Bulk Invoice Data Extractor?",
    "You can select and inspect your PDF batch on the home page without an account. Sign in or create an account before any file is uploaded or processed.",
  ],
  [
    "Which invoice layouts are supported?",
    "Bulk Invoice Data Extractor supports supplier invoices with changing layouts, including digital PDFs and scans. Password-protected or damaged files are flagged instead of silently skipped.",
  ],
  [
    "What can I export?",
    "You can export structured results to Excel, CSV, Google Sheets or JSON. Repeating line items remain usable rows rather than being flattened into unreadable text.",
  ],
  [
    "How many invoices can I process at once?",
    "A dataset can contain up to 300 PDFs. File, page and daily fair-use limits can be adjusted by an administrator.",
  ],
  [
    "Does Bulk Invoice Data Extractor use my documents for training?",
    "No. Your document contents are processed only to provide the extraction service, are not sold, and are not used to train unrelated models or build advertising profiles.",
  ],
  [
    "How long are uploaded files kept?",
    "The default document retention window is 90 days, with shorter retention for generated exports. Administrators can configure retention to meet operational or contractual needs.",
  ],
  [
    "Can I review uncertain values?",
    "Yes. Low-confidence values are placed in a focused review queue with source evidence so you can correct them before export.",
  ],
  [
    "Is there an API?",
    "The processing service is API-based internally. Public API access can be enabled as a managed product setting when your workflow requires automation.",
  ],
];

export const blogPosts = [
  {
    slug: "invoice-data-extraction-guide",
    category: "Guide",
    title: "A practical guide to invoice data extraction",
    excerpt:
      "What reliable extraction looks like, which fields matter, and where human review still adds value.",
    date: "31 August 2026",
    read: "6 min read",
    body: [
      "Invoice extraction is more than copying a total from a PDF. A useful dataset preserves document-level fields such as supplier, invoice number, dates, tax and currency while keeping every line item connected to its source invoice.",
      "The strongest workflow starts with deterministic text extraction, adds OCR for scans, and uses document AI only when layout or meaning cannot be recovered reliably. This layered approach improves privacy, cost control and explainability.",
      "Before exporting, route only uncertain values to review. A focused exception queue is faster and safer than checking every cell or trusting every result blindly.",
    ],
  },
  {
    slug: "pdf-to-excel-without-retyping",
    category: "Workflow",
    title: "How to move invoice PDFs into Excel without retyping",
    excerpt:
      "A repeatable workflow for finance teams handling mixed suppliers, scans and line items.",
    date: "24 August 2026",
    read: "5 min read",
    body: [
      "Start by defining the output you actually need. Supplier, invoice number, issue date, due date, subtotal, tax and total are common document fields; description, quantity, unit price and line total are common repeating fields.",
      "Process related invoices in a single dataset, inspect suggested fields, then correct exceptions before export. This creates a stable column structure even when individual suppliers use different templates.",
      "Use XLSX for human workflows, CSV for imports, Google Sheets for collaboration and JSON when another system will consume the result.",
    ],
  },
  {
    slug: "invoice-processing-data-privacy",
    category: "Privacy",
    title: "What to ask about privacy before uploading invoices",
    excerpt:
      "Retention, subprocessors, training use, access controls and deletion explained in plain language.",
    date: "17 August 2026",
    read: "7 min read",
    body: [
      "Financial documents can contain addresses, tax identifiers, bank details and commercial terms. Before using any extraction service, understand where files are stored, how long they are retained and which subprocessors may receive document-derived content.",
      "A responsible provider should limit processing to the requested service, avoid secondary training or advertising uses, enforce tenant isolation, and provide a clear deletion path.",
      "For regulated or contractual work, review the data processing agreement alongside the privacy policy. The DPA should explain confidentiality, security measures, subprocessors, incident response and deletion or return of personal data.",
    ],
  },
];

function BeforeAfter({
  title,
  imageUrl,
}: {
  title?: string;
  imageUrl?: string;
}) {
  const rows = [
    ["#1024", "Really Great Company", "Content Plan", "$50/hr", "4", "$200.00"],
    ["#1024", "Really Great Company", "Copy Writing", "$50/hr", "2", "$100.00"],
    [
      "#1024",
      "Really Great Company",
      "Website Design",
      "$50/hr",
      "5",
      "$250.00",
    ],
  ];
  return (
    <section id="example" className="bide-example">
      <div className="example-heading">
        <span className="bide-eyebrow">See the transformation</span>
        <h2>{title ?? "One invoice in. Structured data out."}</h2>
        <p>
          Bulk Invoice Data Extractor keeps the source invoice and extracted
          fields side by side, so every value is easy to verify.
        </p>
      </div>
      <div className="example-grid">
        <figure className="before-card">
          <figcaption>
            <span>Before</span>
            <strong>freelancer-invoice.pdf</strong>
          </figcaption>
          <div>
            <img
              src={imageUrl ?? "/invoice-sample.png"}
              alt="Freelancer invoice number 1024 with three service line items and a stated total"
            />
          </div>
        </figure>
        <div className="example-arrow">
          <ArrowRight />
        </div>
        <figure className="after-card">
          <figcaption>
            <span>After</span>
            <strong>invoice-data.xlsx</strong>
          </figcaption>
          <div className="dataset-preview">
            <div className="dataset-toolbar">
              <span>
                <Database size={16} /> Extracted dataset
              </span>
              <small>3 rows · 6 fields</small>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Invoice</th>
                  <th>Billed to</th>
                  <th>Description</th>
                  <th>Rate</th>
                  <th>Hours</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={index}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}>
                        {cell}
                        {cellIndex === 0 || cellIndex === 2 ? (
                          <small>
                            <Check size={11} /> verified
                          </small>
                        ) : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="dataset-facts">
              <span>
                <small>Pay to</small>
                <strong>123 Anywhere St., Any City</strong>
              </span>
              <span>
                <small>Payment terms</small>
                <strong>14 business days</strong>
              </span>
              <span>
                <small>Invoice total</small>
                <strong>$875.00</strong>
              </span>
              <span>
                <small>Line-item sum</small>
                <strong>$550.00</strong>
              </span>
            </div>
            <div className="dataset-status dataset-warning">
              <strong>!</strong> Total differs from the extracted line-item sum.
              Review before export.
            </div>
          </div>
        </figure>
      </div>
    </section>
  );
}

function FaqSection({
  items,
  title,
}: {
  items?: { question: string; answer: string }[];
  title?: string;
}) {
  const displayItems = items?.length
    ? items.map((item) => [item.question, item.answer])
    : faqItems;
  return (
    <section id="faq" className="bide-section faq-section">
      <div className="faq-heading">
        <span className="bide-eyebrow">Frequently asked questions</span>
        <h2>{title ?? "Clear answers before you upload."}</h2>
        <p>Security, formats, limits and what happens to your data.</p>
      </div>
      <div className="faq-grid">
        {displayItems.map(([question, answer]) => (
          <details key={question}>
            <summary>
              {question}
              <span>+</span>
            </summary>
            <p>{answer}</p>
          </details>
        ))}
      </div>
    </section>
  );
}

type DisplayPost = (typeof blogPosts)[number];
function BlogPreview({ posts = blogPosts }: { posts?: DisplayPost[] }) {
  return (
    <section className="blog-preview bide-section">
      <div className="blog-heading">
        <div>
          <span className="bide-eyebrow">From the journal</span>
          <h2>Practical notes on invoice data.</h2>
        </div>
        <Link to="/blog">
          View all articles <ChevronRight size={16} />
        </Link>
      </div>
      <div className="blog-grid">
        {posts.map((post) => (
          <article key={post.slug}>
            <span>{post.category}</span>
            <h3>
              <Link to={`/blog/${post.slug}`}>{post.title}</Link>
            </h3>
            <p>{post.excerpt}</p>
            <footer>
              <small>
                {post.date} · {post.read}
              </small>
              <Link aria-label={`Read ${post.title}`} to={`/blog/${post.slug}`}>
                <ArrowRight size={17} />
              </Link>
            </footer>
          </article>
        ))}
      </div>
    </section>
  );
}

export function BlogIndex() {
  const cms = usePublicCms();
  const posts: DisplayPost[] = cms?.blog_posts.length
    ? cms.blog_posts.map((post) => ({
        ...post,
        date: post.published_at
          ? new Date(post.published_at).toLocaleDateString("en", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })
          : "Published",
        read: `${Math.max(1, Math.ceil(post.content.split(/\s+/).length / 220))} min read`,
        body: post.content.split(/\n\s*\n/).filter(Boolean),
      }))
    : blogPosts;
  return (
    <main className="bide-landing">
      <BideHeader />
      <section className="editorial-hero">
        <span className="bide-eyebrow">The journal</span>
        <h1>Better invoice data, without the jargon.</h1>
        <p>
          Guides for building reliable PDF-to-data workflows and protecting
          financial documents along the way.
        </p>
      </section>
      <section className="bide-section blog-index-grid">
        {posts.map((post) => (
          <article key={post.slug}>
            <span>{post.category}</span>
            <h2>
              <Link to={`/blog/${post.slug}`}>{post.title}</Link>
            </h2>
            <p>{post.excerpt}</p>
            <Link to={`/blog/${post.slug}`}>
              Read article <ArrowRight size={15} />
            </Link>
          </article>
        ))}
      </section>
      <BideFooter />
    </main>
  );
}

export function BlogArticle({ slug }: { slug: string }) {
  const cms = usePublicCms();
  const posts: DisplayPost[] = cms?.blog_posts.length
    ? cms.blog_posts.map((item) => ({
        ...item,
        date: item.published_at
          ? new Date(item.published_at).toLocaleDateString("en", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })
          : "Published",
        read: `${Math.max(1, Math.ceil(item.content.split(/\s+/).length / 220))} min read`,
        body: item.content.split(/\n\s*\n/).filter(Boolean),
      }))
    : blogPosts;
  const post = posts.find((item) => item.slug === slug);
  if (!post)
    return (
      <main className="bide-landing">
        <BideHeader />
        <section className="editorial-hero">
          <h1>Article not found.</h1>
          <Link to="/blog">Return to the journal</Link>
        </section>
        <BideFooter />
      </main>
    );
  return (
    <main className="bide-landing">
      <BideHeader />
      <article className="article-page">
        <span className="bide-eyebrow">{post.category}</span>
        <h1>{post.title}</h1>
        <p className="article-deck">{post.excerpt}</p>
        <p className="article-meta">
          {post.date} · {post.read}
        </p>
        {post.body.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}
        <aside>
          <strong>Ready to try it on your invoices?</strong>
          <Link to="/signup">
            Create a free account <ArrowRight size={15} />
          </Link>
        </aside>
      </article>
      <BideFooter />
    </main>
  );
}

export type PolicyKey =
  | "terms"
  | "privacy"
  | "cookies"
  | "dpa"
  | "acceptable-use"
  | "security"
  | "how";
const policies: Record<
  PolicyKey,
  { title: string; intro: string; sections: [string, string][] }
> = {
  terms: {
    title: "Terms and Conditions",
    intro:
      "The rules that govern access to and use of the Bulk Invoice Data Extractor service.",
    sections: [
      [
        "Using the service",
        "You must provide accurate account information, keep credentials secure and use Bulk Invoice Data Extractor only for lawful purposes. You may process documents only when you have the right or authority to do so.",
      ],
      [
        "Your content",
        "You retain ownership of uploaded documents and extracted data. You grant Bulk Invoice Data Extractor only the limited rights needed to host, process, transmit and delete that content to provide the service.",
      ],
      [
        "Accuracy and review",
        "Extraction may contain errors or omissions. You are responsible for reviewing results before accounting, tax, payment, compliance or business decisions. Bulk Invoice Data Extractor does not provide legal, tax or accounting advice.",
      ],
      [
        "Fair use and availability",
        "Limits protect service availability and processing cost. We may pause abusive, automated or harmful activity. Features may change and background jobs or integrations may occasionally be unavailable.",
      ],
      [
        "Liability and termination",
        "To the extent permitted by law, the service is provided without a guarantee of uninterrupted or error-free operation. You may stop using Bulk Invoice Data Extractor and request account deletion; we may suspend material violations of these terms.",
      ],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    intro:
      "How Bulk Invoice Data Extractor handles account information, financial documents and product usage data.",
    sections: [
      [
        "Data we process",
        "We process account details, uploaded documents, extraction outputs, support messages and limited service telemetry needed to operate and secure Bulk Invoice Data Extractor.",
      ],
      [
        "Why we process it",
        "We use data to provide extraction, review and export features; authenticate users; prevent abuse; support users; maintain security; and meet legal obligations. We do not sell document contents or use them to build advertising profiles.",
      ],
      [
        "Document processing",
        "Local text extraction and OCR are attempted first. When needed, document-derived text or page images may be sent to the configured AI processor solely to complete your requested extraction.",
      ],
      [
        "Retention and deletion",
        "Uploaded documents are scheduled for deletion after the configured retention window, which is 90 days by default. Exports use a shorter window. Account and audit information may be retained where required for security, disputes or law.",
      ],
      [
        "Your choices",
        "You can delete datasets, disconnect integrations, manage cookies and request access, correction, export or deletion of personal data where applicable.",
      ],
    ],
  },
  cookies: {
    title: "Cookie Policy",
    intro:
      "A plain-language list of the browser storage Bulk Invoice Data Extractor uses and the choices available to you.",
    sections: [
      [
        "Essential cookies",
        "Authentication and security cookies keep you signed in, protect sessions and prevent request abuse. The service cannot operate securely without them.",
      ],
      [
        "Preferences",
        "Bulk Invoice Data Extractor may store interface preferences such as dismissed notices or display choices on your device.",
      ],
      [
        "Analytics and advertising",
        "Optional analytics or advertising cookies are disabled unless enabled by configuration and permitted by your consent and region. Document contents are never placed in cookies or advertising events.",
      ],
      [
        "Managing cookies",
        "You can use the cookie controls offered on the site or your browser settings. Blocking essential cookies may prevent sign-in and protected workflows from functioning.",
      ],
    ],
  },
  dpa: {
    title: "Data Processing Agreement",
    intro:
      "The commitments that apply when Bulk Invoice Data Extractor processes personal data in documents on a customer’s behalf.",
    sections: [
      [
        "Roles and instructions",
        "For customer-uploaded document data, the customer is the controller or business and Bulk Invoice Data Extractor acts as processor or service provider. The service processes that data only on documented instructions needed to provide, secure and support the service.",
      ],
      [
        "No secondary use",
        "Bulk Invoice Data Extractor will not sell customer data, use it for unrelated advertising, retain it for an independent commercial purpose, or use document contents to train unrelated models.",
      ],
      [
        "Confidentiality and security",
        "Personnel and subprocessors with access are bound by confidentiality. Bulk Invoice Data Extractor applies access control, encryption in transit and at rest, tenant isolation, logging, secure deletion processes and incident response measures appropriate to the risk.",
      ],
      [
        "Subprocessors and transfers",
        "Bulk Invoice Data Extractor may use infrastructure, authentication, storage and document-AI subprocessors listed in service documentation. The service remains responsible for their data-protection obligations and uses appropriate transfer safeguards where required.",
      ],
      [
        "Requests, incidents and audits",
        "Bulk Invoice Data Extractor will reasonably assist with data-subject requests, security incidents, impact assessments and evidence of compliance. Customers remain responsible for lawful instructions, notices and source-document permissions.",
      ],
      [
        "Return and deletion",
        "At the end of service, Bulk Invoice Data Extractor will delete or return customer personal data according to configured retention and legal requirements. Backup or security copies are isolated and expire under documented schedules.",
      ],
    ],
  },
  "acceptable-use": {
    title: "Acceptable Use Policy",
    intro:
      "The boundaries that keep Bulk Invoice Data Extractor safe, reliable and lawful for everyone.",
    sections: [
      [
        "Prohibited content and conduct",
        "Do not upload unlawfully obtained documents, malware, exploit material or content that infringes rights. Do not use Bulk Invoice Data Extractor to facilitate fraud, identity theft, discrimination or illegal surveillance.",
      ],
      [
        "Service integrity",
        "Do not probe for vulnerabilities, bypass limits, interfere with other users, scrape the service at harmful rates or attempt to access another tenant’s data.",
      ],
      [
        "Automation",
        "Use approved APIs and reasonable rates for automated workflows. Browser automation intended to evade controls or create abusive load is prohibited.",
      ],
      [
        "Enforcement",
        "We may rate-limit, suspend or terminate activity that creates security, legal or availability risk, and may preserve evidence where required by law.",
      ],
    ],
  },
  security: {
    title: "Security Overview",
    intro:
      "The safeguards designed to protect financial documents throughout the extraction workflow.",
    sections: [
      [
        "Access and isolation",
        "Authenticated, server-side authorization is applied to protected operations. Dataset and document queries are scoped to the owner, with administrative access separately allowlisted.",
      ],
      [
        "Encryption and secrets",
        "Files and application data use encryption in transit and at rest. Service credentials and integration tokens remain server-side and are not embedded in the browser.",
      ],
      [
        "Data minimization",
        "Only the document content needed for extraction is processed. Advertising surfaces are kept away from document, review and viewer screens.",
      ],
      [
        "Monitoring and response",
        "Security headers, validation, rate controls, job auditing and retention reconciliation reduce common web and processing risks. Suspected incidents follow an investigation, containment and notification process.",
      ],
    ],
  },
  how: {
    title: "How Bulk Invoice Data Extractor works",
    intro:
      "A transparent path from an invoice PDF to a reviewed, export-ready dataset.",
    sections: [
      [
        "1. Upload",
        "Select up to 300 PDFs. Files are validated before private upload and remain isolated to your account.",
      ],
      [
        "2. Pick fields",
        "The extractor inspects representative documents and suggests useful document fields and repeating line-item fields.",
      ],
      [
        "3. Extract and review",
        "Text extraction and OCR run first. Difficult interpretation is escalated only when needed, and uncertain values are queued with source evidence.",
      ],
      [
        "4. Export",
        "Once reviewed, download one consistent Excel, CSV or JSON dataset, or send it to Google Sheets.",
      ],
    ],
  },
};

export function PolicyPage({ type }: { type: PolicyKey }) {
  const policy = policies[type];
  return (
    <main className="bide-landing">
      <BideHeader />
      <article className="policy-page">
        <span className="bide-eyebrow">Product policies</span>
        <h1>{policy.title}</h1>
        <p className="policy-intro">{policy.intro}</p>
        {policy.sections.map(([title, body]) => (
          <section key={title}>
            <h2>{title}</h2>
            <p>{body}</p>
          </section>
        ))}
        <p className="policy-updated">Last updated: 31 August 2026</p>
      </article>
      <BideFooter />
    </main>
  );
}

export function BideFooter() {
  return (
    <footer className="bide-footer">
      <div>
        <BideBrand />
        <p>
          Private, reviewable invoice extraction for teams that want clean data
          without manual retyping.
        </p>
      </div>
      <div>
        <strong>Product</strong>
        <Link to="/#how">How it works</Link>
        <Link to="/#example">Example</Link>
        <Link to="/#faq">FAQ</Link>
        <Link to="/blog">Blog</Link>
      </div>
      <div>
        <strong>Legal & trust</strong>
        <Link to="/terms">Terms</Link>
        <Link to="/privacy">Privacy</Link>
        <Link to="/cookie-policy">Cookies</Link>
        <Link to="/data-processing-agreement">Data processing</Link>
        <Link to="/acceptable-use">Acceptable use</Link>
        <Link to="/security">Security</Link>
      </div>
      <small>© 2026 Bulk Invoice Data Extractor. All rights reserved.</small>
    </footer>
  );
}
